import React from 'react';
import { FileText } from 'lucide-react';
import { dpsTheme } from '../../../styles/dpsTheme';
import { ImprovementAction } from '../../../services/progressTrackingService';

interface TimelineViewProps {
  actions: ImprovementAction[];
}

export const TimelineView: React.FC<TimelineViewProps> = ({ actions }) => {
  const getPriorityConfig = (priority: 'high' | 'medium' | 'low') => {
    return dpsTheme.colors.priority[priority];
  };

  const getPriorityLabel = (priority: string) => {
    if (priority === 'high') return 'Haute priorité';
    if (priority === 'medium') return 'Moyenne priorité';
    return 'Basse priorité';
  };

  const getCategoryBadgeStyle = (category: string) => {
    if (category === 'protection') {
      return {
        background: 'rgba(59, 130, 246, 0.15)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        color: '#60a5fa'
      };
    }
    if (category === 'surveillance') {
      return {
        background: 'rgba(139, 92, 246, 0.15)',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        color: '#a78bfa'
      };
    }
    return {
      background: 'rgba(245, 158, 11, 0.15)',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      color: '#fbbf24'
    };
  };

  const getCategoryLabel = (category: string) => {
    if (category === 'protection') return 'Protection';
    if (category === 'surveillance') return 'Surveillance';
    return 'Dissuasion';
  };

  const getStatusLabel = (status: string) => {
    if (status === 'completed') return 'Réalisée';
    if (status === 'in_progress') return 'En cours';
    if (status === 'planned') return 'Planifiée';
    return 'Non planifiée';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Date non définie';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isCompleted = (action: ImprovementAction) => action.status === 'completed';

  if (actions.length === 0) {
    return (
      <section style={{
        background: dpsTheme.colors.background.card,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${dpsTheme.colors.border.primary}`,
        borderRadius: dpsTheme.borderRadius.lg,
        padding: dpsTheme.spacing['2xl'],
        marginBottom: dpsTheme.spacing['2xl'],
        textAlign: 'center'
      }}>
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke={dpsTheme.colors.text.muted}
          strokeWidth="1"
          style={{ margin: '0 auto 16px', opacity: 0.5 }}
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        <div style={{
          fontSize: dpsTheme.typography.fontSize.lg,
          fontWeight: dpsTheme.typography.fontWeight.semibold,
          color: dpsTheme.colors.text.secondary,
          marginBottom: '8px'
        }}>
          Aucune action dans l'historique
        </div>
        <div style={{
          fontSize: dpsTheme.typography.fontSize.sm,
          color: dpsTheme.colors.text.muted
        }}>
          Les actions que vous planifiez et réalisez apparaîtront ici
        </div>
      </section>
    );
  }

  return (
    <section style={{
      background: dpsTheme.colors.background.card,
      backdropFilter: 'blur(20px)',
      border: `1px solid ${dpsTheme.colors.border.primary}`,
      borderRadius: dpsTheme.borderRadius.lg,
      padding: dpsTheme.spacing['2xl'],
      marginBottom: dpsTheme.spacing['2xl']
    }}>
      <h2 style={{
        fontSize: dpsTheme.typography.fontSize.xl,
        fontWeight: dpsTheme.typography.fontWeight.semibold,
        color: dpsTheme.colors.text.primary,
        marginBottom: dpsTheme.spacing.xl,
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
        Timeline des actions
      </h2>

      <div style={{
        position: 'relative',
        paddingLeft: '40px'
      }}>
        <div style={{
          content: '',
          position: 'absolute',
          left: '16px',
          top: 0,
          bottom: 0,
          width: '2px',
          background: dpsTheme.colors.border.primary
        }} />

        {actions.map((action) => {
          const priorityConfig = getPriorityConfig(action.priority);
          const categoryStyle = getCategoryBadgeStyle(action.action_category);
          const completed = isCompleted(action);

          return (
            <div
              key={action.id}
              style={{
                position: 'relative',
                marginBottom: dpsTheme.spacing.xl
              }}
            >
              <div style={{
                position: 'absolute',
                left: '-40px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: completed
                  ? 'rgba(16, 185, 129, 0.2)'
                  : 'rgba(59, 130, 246, 0.2)',
                border: `2px solid ${completed ? '#10b981' : '#3b82f6'}`,
                color: completed ? '#10b981' : '#3b82f6'
              }}>
                {completed ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                )}
              </div>

              <div
                style={{
                  background: dpsTheme.colors.background.darker,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  borderLeft: `3px solid ${completed ? '#10b981' : '#3b82f6'}`,
                  borderRadius: dpsTheme.borderRadius.md,
                  padding: dpsTheme.spacing.xl,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15, 23, 42, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = dpsTheme.colors.background.darker;
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div>
                    <div style={{
                      fontSize: dpsTheme.typography.fontSize.lg,
                      fontWeight: dpsTheme.typography.fontWeight.semibold,
                      color: dpsTheme.colors.text.primary,
                      marginBottom: '4px'
                    }}>
                      {action.action_title}
                    </div>
                    <div style={{
                      fontSize: dpsTheme.typography.fontSize.sm,
                      color: dpsTheme.colors.text.muted
                    }}>
                      {completed && action.implemented_at
                        ? `Réalisée le ${formatDate(action.implemented_at)}`
                        : `Créée le ${formatDate(action.created_at)}`}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '10px',
                    fontWeight: dpsTheme.typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: priorityConfig.bg,
                    border: `1px solid ${priorityConfig.border}`,
                    color: priorityConfig.text
                  }}>
                    {getPriorityLabel(action.priority)}
                  </span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: dpsTheme.spacing.lg,
                  marginBottom: dpsTheme.spacing.lg
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{
                      fontSize: '11px',
                      color: dpsTheme.colors.text.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Coût estimé
                    </span>
                    <span style={{
                      fontSize: dpsTheme.typography.fontSize.lg,
                      fontWeight: dpsTheme.typography.fontWeight.semibold,
                      color: dpsTheme.colors.text.primary
                    }}>
                      {action.estimated_cost.toLocaleString('fr-FR')} €
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{
                      fontSize: '11px',
                      color: dpsTheme.colors.text.muted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Coût réel
                    </span>
                    <span style={{
                      fontSize: dpsTheme.typography.fontSize.lg,
                      fontWeight: dpsTheme.typography.fontWeight.semibold,
                      color: dpsTheme.colors.text.primary
                    }}>
                      {action.actual_cost
                        ? `${action.actual_cost.toLocaleString('fr-FR')} €`
                        : '—'}
                    </span>
                  </div>
                </div>

                {action.notes && (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: dpsTheme.borderRadius.sm,
                    padding: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      fontSize: dpsTheme.typography.fontSize.xs,
                      fontWeight: dpsTheme.typography.fontWeight.semibold,
                      color: dpsTheme.colors.text.muted,
                      marginBottom: '6px'
                    }}>
                      <FileText size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '5px' }} />Notes :
                    </div>
                    <div style={{
                      fontSize: dpsTheme.typography.fontSize.sm,
                      color: dpsTheme.colors.text.tertiary,
                      lineHeight: dpsTheme.typography.lineHeight.relaxed
                    }}>
                      {action.notes}
                    </div>
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: dpsTheme.spacing.lg,
                  fontSize: dpsTheme.typography.fontSize.sm,
                  color: dpsTheme.colors.text.muted
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {completed ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                    )}
                    <span>{getStatusLabel(action.status)}</span>
                  </div>
                  <span>•</span>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: dpsTheme.typography.fontWeight.semibold,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    ...categoryStyle
                  }}>
                    {getCategoryLabel(action.action_category)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};